import { ChevronDown, ChevronTop, WarningCircle } from 'assets/icons';
import Badge from 'components/badge/badge';
import Button from 'components/button';
import ModalBody from 'components/modal/components/body';
import ModalFooter from 'components/modal/components/footer';
import Modalheader from 'components/modal/components/header';
import useModal from 'components/modal/useModal';
import StyledInput from 'components/styledInput/StyledInput';
import PillTabs from 'components/tabs/PillTabs';
import UseContext from 'hooks/useContext';
import { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import ApiDatabase from '../../../../../server';

const STEPS = { BONUS: 'bonus', UNIT: 'unit', CONFIRMATION: 'confirmation' };
const STEP_ORDER = [STEPS.BONUS, STEPS.UNIT, STEPS.CONFIRMATION];

/**
 * `nature` comes straight from BeSTT and is what separates the two kinds of
 * line: a « Soumis » element is billed at the mission rate — a prime — while a
 * « Non Soumis » one is billed at 1 — an indemnité. The rubric label cannot be
 * used instead: some are plainly misnamed (« INDEMNITE PANIER SOUMIS »), and
 * `nature` is the field that decides how the line is actually charged.
 *
 * Anything else (« Calcul » aggregates, employer charges with no nature) is a
 * payroll line that has no business in this picker.
 */
const NATURE_PRIME = 'Soumis';
const NATURE_ALLOWANCE = 'Non Soumis';
const SELECTABLE_NATURES = [NATURE_PRIME, NATURE_ALLOWANCE];

/**
 * What may be added here as a free line, read off the referential coefficient
 * rather than off a list of labels:
 *
 * - `> 1` is a whole pay rate, not a majoration — the « HEURES SUPP… » family,
 *   configured on its own screens. A label list used to hold these back and let
 *   « Heures supplémentaires de nuit 25% » through on a spelling difference.
 * - `<= 0` is a retenue or a state aid (« RETENUE TROP PERCU » at -1, `SATD`
 *   at 0). Those are billed at neither the mission rate nor 1, and forcing a
 *   coefficient of 1 on a retenue would flip its sign on the invoice.
 */
const isSelectableCoefficient = (coefficient) =>
  coefficient > 0 && coefficient <= 1;

/**
 * Meal vouchers have their own card on the company sheet, where the unit value
 * of a ticket can be entered — a field this form does not have. Matched on the
 * BeSTT code rather than the label, which carries a trailing space on both.
 */
const RESTAURANT_TICKET_CODES = [755, 763];

/** Section order of the unit picker; anything unknown is appended after. */
const UNIT_GROUP_ORDER = [
  'Journées',
  'Heures',
  'Exemples de formules',
  'Exemples de tranches',
  'Autres',
];

/** Chips shown under the unit field; more would crowd the modal footer. */
const MAX_UNIT_SUGGESTIONS = 3;

/** How often a rubric has been used, all units taken together. */
const timesUsed = (bonus) =>
  (bonus.unit_usage ?? []).reduce(
    (total, entry) => total + (entry.count ?? 0),
    0,
  );

/**
 * Most used first, then alphabetical. The counters make the everyday rubrics
 * reachable without scrolling, while the ones nobody has used yet — the large
 * majority — stay in an order the eye can search.
 *
 * Names are trimmed and compared without case or accents: the referential
 * holds « PRIME OUTILLAGE » next to « Prime parisienne », and a raw sort would
 * file them in separate blocks.
 */
const byPopularityThenName = (a, b) =>
  timesUsed(b) - timesUsed(a) ||
  (a.name ?? '')
    .trim()
    .localeCompare((b.name ?? '').trim(), 'fr', { sensitivity: 'base' });

const isPrime = (bonus) => bonus?.nature === NATURE_PRIME;

/**
 * The referential coefficient is a pay multiplier whose meaning depends on the
 * rubric family: on a « MAJ… » line it carries the majoration alone (0.3 for
 * +30 %), while on an « HEURES SUPP… » line it carries the whole rate (1.25 for
 * +25 %). Only the first reading is a majoration, so only a coefficient
 * strictly between 0 and 1 prefills the field — a coefficient of 1 means « no
 * majoration » on 188 of the 228 selectable rubrics, and offering 100 % there
 * would be wrong far more often than right.
 */
const defaultMajoration = (bonus) => {
  const coefficient = bonus?.coefficient;
  if (!(coefficient > 0 && coefficient < 1)) return '';
  return Math.round(coefficient * 10000) / 100;
};

const UnitRow = ({ unit, isSelected, onSelect }) => (
  <button
    type={'button'}
    data-unit-id={unit._id}
    onClick={() => onSelect(unit)}
    className={`w-full flex items-center gap-4 px-4 py-3 text-left border-b border-gray-100 transition-colors ${
      isSelected
        ? 'border border-yellow-500 bg-yellow-50'
        : 'bg-white hover:bg-gray-50'
    }`}
  >
    <span className={'flex-1 min-w-0 truncate text-gray-700'}>
      {unit.description || unit.name}
    </span>
    <Badge type={'SECONDARY'}>{unit.name}</Badge>
  </button>
);

const UnitGroup = ({
  group,
  units,
  isCollapsed,
  onToggle,
  selectedUnitId,
  onSelect,
}) => (
  <div>
    <button
      type={'button'}
      onClick={onToggle}
      className={
        'w-full flex items-center gap-1 px-4 py-2.5 bg-gray-50 text-gray-900 uppercase'
      }
    >
      <span className={'flex-1 text-left truncate'}>{group}</span>
      {isCollapsed ? (
        <ChevronDown wh={20} color={'#374151'} />
      ) : (
        <ChevronTop wh={20} color={'#374151'} />
      )}
    </button>
    {!isCollapsed &&
      units.map((unit) => (
        <UnitRow
          key={unit._id}
          unit={unit}
          isSelected={unit._id === selectedUnitId}
          onSelect={onSelect}
        />
      ))}
  </div>
);

const RecapRow = ({ label, children }) => (
  <div
    className={
      'flex items-center gap-3 py-3.5 pl-3 pr-4 border-b border-gray-100'
    }
  >
    <span className={'flex-1 min-w-0 truncate text-gray-900'}>{label}</span>
    <span className={'text-right text-gray-700 truncate'}>{children}</span>
  </div>
);

const ModalEditBonusesAndAllowances = () => {
  const { toggle } = useModal();
  const { modalData, setModalDataReturn } = UseContext();
  const token = localStorage.getItem('xsrfToken');
  const isEdition = !!modalData.idBonusesAndAllowances;

  const [step, setStep] = useState(STEPS.BONUS);
  const [isWaiting, setIsWaiting] = useState(false);
  const [isWaitingBonusList, setIsWaitingBonusList] = useState(false);
  const [isWaitingBonusUnitList, setIsWaitingBonusUnitList] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [bonusList, setBonusList] = useState([]);
  const [bonusUnitList, setBonusUnitList] = useState([]);
  const [collapsedGroups, setCollapsedGroups] = useState([]);
  // The code typed or picked in the footer field is what drives the unit step:
  // the list above filters on it, and the step only validates once it names a
  // unit of the referential.
  const [unitSearch, setUnitSearch] = useState('');
  const unitListRef = useRef(null);
  const [bonusesAndAllowances, setBonusesAndAllowances] = useState({
    name: '',
    unit: '',
    payed: '',
    coef: '',
  });
  const [errors, setErrors] = useState({ name: '', payed: '', unit: '' });

  // Rubrics the company already carries, handed over by the sheet that opens
  // this modal. Only used to warn: the same rubric with two different units is
  // a legitimate setup, and only the person configuring it can tell.
  const configuredBonusNames = modalData.configuredBonusNames ?? [];

  const selectedBonus = bonusList.find(
    (bonus) => bonus._id === bonusesAndAllowances.name,
  );
  // What the rubric is most often paired with, most frequent first. The
  // counters come from production contracts and grow with every save here, so
  // the order follows real practice. Never a default: the unit belongs to the
  // agreement with the company, so the same rubric legitimately differs from
  // one to the next — a rubric nobody has used yet simply offers nothing.
  const alreadyConfigured =
    !isEdition &&
    !!selectedBonus &&
    configuredBonusNames.some(
      (name) => name?.trim() === selectedBonus.name?.trim(),
    );

  const suggestedUnits = [...(selectedBonus?.unit_usage ?? [])]
    .sort((a, b) => (b.count ?? 0) - (a.count ?? 0))
    .map((entry) => entry.unit)
    .filter((code) => bonusUnitList.some((unit) => unit.name === code))
    .slice(0, MAX_UNIT_SUGGESTIONS)
    .map((code) => ({ name: code }));

  const matchedUnit = useMemo(() => {
    const search = unitSearch.trim().toLowerCase();
    if (!search) return null;
    return bonusUnitList.find((unit) => unit.name?.toLowerCase() === search);
  }, [unitSearch, bonusUnitList]);

  const groupedUnits = useMemo(() => {
    const search = unitSearch.trim().toLowerCase();
    // Filtering narrows the list while the code is being typed, but stops
    // once it names an existing unit: the choice is then marked by the
    // highlight, and the whole list stays browsable so another can be picked
    // without clearing the field first.
    const matches = (unit) =>
      matchedUnit ||
      !search ||
      unit.name?.toLowerCase().includes(search) ||
      unit.description?.toLowerCase().includes(search);

    const groups = new Map();
    bonusUnitList.filter(matches).forEach((unit) => {
      const group = unit.group || 'Autres';
      groups.set(group, [...(groups.get(group) ?? []), unit]);
    });

    return [...groups.entries()].sort(([a], [b]) => {
      const rankA = UNIT_GROUP_ORDER.indexOf(a);
      const rankB = UNIT_GROUP_ORDER.indexOf(b);
      if (rankA === -1 && rankB === -1) return a.localeCompare(b);
      if (rankA === -1) return 1;
      if (rankB === -1) return -1;
      return rankA - rankB;
    });
  }, [bonusUnitList, unitSearch, matchedUnit]);

  useEffect(() => {
    if (!modalData.idCompany) return;

    setIsWaitingBonusList(true);
    ApiDatabase.getBonusListCompany(
      { token, idCompany: modalData.idCompany },
      (data) => {
        setBonusList(
          data.bonusList
            .filter(
              (bonus) =>
                SELECTABLE_NATURES.includes(bonus.nature) &&
                isSelectableCoefficient(bonus.coefficient) &&
                !RESTAURANT_TICKET_CODES.includes(bonus.id_bestt),
            )
            .sort(byPopularityThenName),
        );
        setIsWaitingBonusList(false);
      },
      (err) => {
        if (err.name !== 'CanceledError') {
          console.error(err);
          setIsWaitingBonusList(false);
        }
      },
    );

    setIsWaitingBonusUnitList(true);
    ApiDatabase.getBonusUnitListCompany(
      { token, idCompany: modalData.idCompany },
      (data) => {
        setBonusUnitList(data.bonusUnitList);
        setIsWaitingBonusUnitList(false);
      },
      (err) => {
        if (err.name !== 'CanceledError') {
          console.error(err);
          setIsWaitingBonusUnitList(false);
        }
      },
    );
  }, [modalData.idCompany]);

  useEffect(() => {
    if (!modalData.idCompany || !modalData.idBonusesAndAllowances) return;

    setIsWaiting(true);
    ApiDatabase.getBonusAndAllowance(
      {
        token,
        idCompany: modalData.idCompany,
        idBonusesAndAllowances: modalData.idBonusesAndAllowances,
      },
      (data) => {
        if (data.bonusAndAllowance) {
          setBonusesAndAllowances(data.bonusAndAllowance);
        }
        setIsWaiting(false);
      },
      (err) => {
        if (err.name !== 'CanceledError') {
          console.error(err);
          setIsWaiting(false);
        }
      },
    );
  }, [modalData.idCompany, modalData.idBonusesAndAllowances]);

  // On edition the unit arrives as an id; the footer field shows codes, so it
  // is seeded once the referential is there to resolve it.
  useEffect(() => {
    if (unitSearch || !bonusesAndAllowances.unit) return;
    const unit = bonusUnitList.find(
      (item) => item._id === bonusesAndAllowances.unit,
    );
    if (unit) setUnitSearch(unit.name);
  }, [bonusUnitList, bonusesAndAllowances.unit]);

  const isBonusStepValid = () => {
    const newErrors = {
      name: bonusesAndAllowances.name ? '' : 'Veuillez sélectionner une prime',
      payed:
        bonusesAndAllowances.payed === '' ||
        bonusesAndAllowances.payed === null ||
        bonusesAndAllowances.payed <= 0
          ? 'La majoration doit être supérieure à 0'
          : '',
    };
    setErrors((prev) => ({ ...prev, ...newErrors }));
    return !newErrors.name && !newErrors.payed;
  };

  const isUnitStepValid = () => {
    const error = matchedUnit
      ? ''
      : 'Veuillez choisir une unité de la liste ou saisir un code existant';
    setErrors((prev) => ({ ...prev, unit: error }));
    return !error;
  };

  const goNext = () => {
    if (step === STEPS.BONUS) {
      if (!isBonusStepValid()) return;
      return setStep(STEPS.UNIT);
    }
    if (step === STEPS.UNIT) {
      if (!isUnitStepValid()) return;
      setBonusesAndAllowances((prev) => ({ ...prev, unit: matchedUnit._id }));
      setStep(STEPS.CONFIRMATION);
    }
  };

  const goBack = () =>
    setStep(STEP_ORDER[Math.max(0, STEP_ORDER.indexOf(step) - 1)]);

  const selectUnit = (unit) => {
    setUnitSearch(unit.name);
    setErrors((prev) => ({ ...prev, unit: '' }));
    // A unit picked from a suggestion may sit in a section the user has
    // folded; scrolling to a hidden row would land on nothing.
    if (unit.group) {
      setCollapsedGroups((prev) =>
        prev.filter((group) => group !== unit.group),
      );
    }
  };

  // Bring the matching row into view — on selection, and on arriving at the
  // step with a unit already chosen. `step` belongs in the dependencies for
  // that second case: on edition the unit is known long before the list is
  // rendered, and the container is still null when the match is first set.
  useEffect(() => {
    if (step !== STEPS.UNIT || !matchedUnit) return;
    unitListRef.current
      ?.querySelector(`[data-unit-id="${matchedUnit._id}"]`)
      ?.scrollIntoView({ block: 'nearest' });
  }, [matchedUnit, step]);

  const toggleGroup = (group) =>
    setCollapsedGroups((prev) =>
      prev.includes(group)
        ? prev.filter((item) => item !== group)
        : [...prev, group],
    );

  const handleSubmit = () => {
    if (!modalData.idCompany || !selectedBonus || !matchedUnit) return;

    setIsLoading(true);
    // A prime follows the mission rate, so its coefficient is left unset and
    // resolved at contract time; an indemnité is always billed at 1.
    const payload = {
      ...bonusesAndAllowances,
      unit: matchedUnit._id,
      coef: isPrime(selectedBonus) ? null : 1,
    };

    const onSuccess = (message, type) => (data) => {
      setIsLoading(false);
      toast.success(message);
      setModalDataReturn({ type, bonusesAndAllowances: data.bonus });
      toggle();
    };
    const onError = (err) => {
      if (err.name !== 'CanceledError') {
        console.error(err);
        setIsLoading(false);
      }
    };

    if (isEdition) {
      ApiDatabase.postUpdBonusesAndAllowances(
        {
          token,
          idCompany: modalData.idCompany,
          idBonusesAndAllowances: modalData.idBonusesAndAllowances,
          bonusesAndAllowances: payload,
        },
        onSuccess(
          'Les primes ont été mises à jour',
          'bonuses_and_allowances_updated',
        ),
        onError,
      );
    } else {
      ApiDatabase.postAddBonusesAndAllowances(
        {
          token,
          idCompany: modalData.idCompany,
          bonusesAndAllowances: payload,
        },
        onSuccess(
          'Les primes ont été ajoutées',
          'bonuses_and_allowances_added',
        ),
        onError,
      );
    }
  };

  const isWaitingAnything =
    isWaiting || isWaitingBonusList || isWaitingBonusUnitList;

  return (
    <>
      <Modalheader hide={toggle} hideShow={true}>
        {isEdition
          ? 'Modifier une prime & indemnité'
          : 'Ajouter une prime & indemnité'}
      </Modalheader>
      <div className={'px-6 py-3'}>
        <PillTabs
          tabs={[
            { id: STEPS.BONUS, label: 'Prime' },
            { id: STEPS.UNIT, label: 'Unités' },
            { id: STEPS.CONFIRMATION, label: 'Confirmation' },
          ]}
          activeTab={step}
          // Steps can only be revisited backwards: jumping ahead would skip the
          // validation each step owns.
          onChange={(tabId) => {
            if (STEP_ORDER.indexOf(tabId) < STEP_ORDER.indexOf(step)) {
              setStep(tabId);
            }
          }}
        />
      </div>
      <ModalBody>
        {isWaitingAnything ? (
          <div className="flex flex-col gap-2.5 text-sm animate-pulse">
            <div className={'h-16 bg-gray-200 rounded w-full'}></div>
            <div className={'h-16 bg-gray-200 rounded w-full'}></div>
            <div className={'h-16 bg-gray-200 rounded w-full'}></div>
          </div>
        ) : (
          <>
            {step === STEPS.BONUS && (
              <div className="flex flex-col gap-2.5 text-sm">
                <StyledInput
                  type="selectMultipleRadio"
                  label="Sélectionner une prime"
                  value={selectedBonus?.name || '-'}
                  list={bonusList.map((bonus) => ({
                    ...bonus,
                    value: bonus._id,
                    label: bonus.name,
                    checked: bonus._id === bonusesAndAllowances.name,
                  }))}
                  onChange={(updatedList) => {
                    const checked = updatedList.find((item) => item.checked);
                    // Changing the rubric restarts the line: the majoration
                    // follows the new one's own rate rather than keeping a
                    // figure that belonged to the previous choice.
                    setBonusesAndAllowances((prev) => ({
                      ...prev,
                      name: checked?.value || '',
                      payed: defaultMajoration(checked),
                    }));
                    setErrors((prev) => ({
                      ...prev,
                      name: checked?.value
                        ? ''
                        : 'Veuillez sélectionner une prime',
                      payed: '',
                    }));
                  }}
                  isSearchable
                  error={errors.name}
                  dropdownFullWidth
                />
                {alreadyConfigured && (
                  <div className={'flex items-center gap-1 text-yellow-600'}>
                    <WarningCircle wh={16} color={'#CA8A04'} />
                    Cette prime est déjà configurée pour cette entreprise.
                  </div>
                )}
                <StyledInput
                  label={'Majoration'}
                  type={'number'}
                  unit={'%'}
                  step="0.01"
                  value={bonusesAndAllowances.payed}
                  onChange={(e) => {
                    const value = e.target.value;
                    setBonusesAndAllowances((prev) => ({
                      ...prev,
                      payed: value !== '' ? parseFloat(value) : '',
                    }));
                  }}
                  error={errors.payed}
                />
              </div>
            )}

            {step === STEPS.UNIT && (
              <div className={'flex flex-col gap-2 text-sm'}>
                {/* The modal's own height is unbounded, so the list is what
                    scrolls — otherwise five sections push the footer off
                    screen. overflow-x stays hidden to keep the rounded
                    corners clipping the rows. */}
                <div
                  ref={unitListRef}
                  className={
                    'border border-gray-100 rounded-md overflow-x-hidden overflow-y-auto max-h-[50vh]'
                  }
                >
                  {groupedUnits.length === 0 ? (
                    <div className={'px-4 py-6 text-center text-gray-500'}>
                      Aucune unité ne correspond à « {unitSearch} »
                    </div>
                  ) : (
                    groupedUnits.map(([group, units]) => (
                      <UnitGroup
                        key={group}
                        group={group}
                        units={units}
                        isCollapsed={collapsedGroups.includes(group)}
                        onToggle={() => toggleGroup(group)}
                        selectedUnitId={matchedUnit?._id}
                        onSelect={selectUnit}
                      />
                    ))
                  )}
                </div>
                {errors.unit && (
                  <div className={'text-red-500'}>{errors.unit}</div>
                )}
              </div>
            )}

            {step === STEPS.CONFIRMATION && (
              <div className={'flex flex-col text-sm'}>
                <RecapRow label={'Type'}>
                  {isPrime(selectedBonus) ? 'Prime' : 'Indemnité'}
                </RecapRow>
                <RecapRow label={'Majoration'}>
                  {bonusesAndAllowances.payed}%
                </RecapRow>
                <RecapRow label={'Unité de calcul'}>
                  {matchedUnit?.description} ({matchedUnit?.name})
                </RecapRow>
                <RecapRow label={'Coefficient'}>
                  {isPrime(selectedBonus)
                    ? 'Variable selon la mission'
                    : '1.00'}
                </RecapRow>
                <RecapRow label={'Facturé (HT)'}>
                  Calculé selon la mission
                </RecapRow>
              </div>
            )}
          </>
        )}
      </ModalBody>
      <ModalFooter>
        {step === STEPS.UNIT && (
          <div className={'mr-auto w-[280px] shrink'}>
            {/* `mr-auto` rather than `flex-1`: the footer is justify-end, and
                a growing child would leave no free space for the margin to
                absorb, parking the field beside the buttons instead of on the
                left. */}
            <StyledInput
              type={'text'}
              placeholder={'Unité'}
              value={unitSearch}
              onChange={(e) => setUnitSearch(e.target.value)}
              listSuggestions={suggestedUnits}
              // A chip only carries a code; the referential entry is what
              // knows which section holds it.
              onClickSuggestion={(suggestion) =>
                selectUnit(
                  bonusUnitList.find((unit) => unit.name === suggestion.name) ??
                    suggestion,
                )
              }
            />
          </div>
        )}
        {step === STEPS.BONUS ? (
          <Button size={'LG'} color={'SECONDARY'} onClick={toggle}>
            Annuler
          </Button>
        ) : (
          <Button size={'LG'} color={'SECONDARY'} onClick={goBack}>
            Retour
          </Button>
        )}
        {step === STEPS.CONFIRMATION ? (
          <Button
            size={'LG'}
            color={isLoading ? 'DISABLED' : 'PRIMARY'}
            onClick={isLoading ? undefined : handleSubmit}
          >
            {isLoading ? 'Confirmation ...' : 'Confirmer'}
          </Button>
        ) : (
          <Button size={'LG'} color={'PRIMARY'} onClick={goNext}>
            Suivant
          </Button>
        )}
      </ModalFooter>
    </>
  );
};

export default ModalEditBonusesAndAllowances;
